import React, { useEffect, useMemo, useRef, useState } from "react";
import { Field } from "formik";
import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import TextField from "@mui/material/TextField";
import { createFilterOptions } from "@mui/material/Autocomplete";

import CustomPaperComponent from "./CustomPaperComponent";
import PopperComponent from "./PopperComponent";
import SelectAllContext from "./SelectAllContext";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const filter = createFilterOptions();

export default function CostCenterAutocomplete({ name, availableCostCenters }) {
  return (
    <Field name={name}>
      {({ field, form }) => (
        <CostCenterAutocompleteInner
          field={field}
          form={form}
          availableCostCenters={availableCostCenters}
        />
      )}
    </Field>
  );
}

function CostCenterAutocompleteInner({ field, form, availableCostCenters }) {
  const filteredOptionsRef = useRef([]);
  const hasFieldValues = (field.value || []).length > 0;
  const [showAll, setShowAll] = useState(!hasFieldValues);

  useEffect(() => {
    if (availableCostCenters.length === 0) return;

    const validIds = (field.value || []).filter(id =>
      availableCostCenters.some(cc => cc.id === id)
    );
    if (validIds.length !== (field.value || []).length) {
      form.setFieldValue(field.name, validIds);
    }
  }, [availableCostCenters]);

  const displayOptions = useMemo(() => {
    if (showAll || !hasFieldValues) return availableCostCenters;
    return availableCostCenters.filter(cc => (field.value || []).includes(cc.id));
  }, [showAll, hasFieldValues, availableCostCenters, field.value]);

  const getAllFilteredSelected = () => {
    if (filteredOptionsRef.current.length === 0) return false;
    return filteredOptionsRef.current.every(opt => (field.value || []).includes(opt.id));
  };

  const handleSelectAll = () => {
    const allSelected = getAllFilteredSelected();
    const filteredIds = filteredOptionsRef.current.map(opt => opt.id);
    if (allSelected) {
      form.setFieldValue(field.name, (field.value || []).filter(id => !filteredIds.includes(id)));
    } else {
      form.setFieldValue(field.name, [...new Set([...(field.value || []), ...filteredIds])]);
    }
  };

  const customFilterOptions = (options, params) => {
    const filtered = filter(options, params);
    filteredOptionsRef.current = filtered;
    return filtered;
  };

  return (
    <SelectAllContext.Provider
      value={{
        multiple: true,
        handleSelectAll,
        getAllFilteredSelected,
        filteredOptionsRef,
        showAllProjects: showAll,
        toggleShowAllProjects: () => setShowAll(prev => !prev),
        hasFieldValues,
        selectAllDisabled: false,
        viewAllLabel: "Cost Centers",
      }}
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={displayOptions}
        getOptionLabel={option => option.label}
        groupBy={option => option.projectName}
        filterOptions={customFilterOptions}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        value={availableCostCenters.filter(cc => (field.value || []).includes(cc.id))}
        onChange={(_, newValue) => {
          form.setFieldValue(field.name, newValue.map(cc => cc.id));
        }}
        PaperComponent={CustomPaperComponent}
        PopperComponent={PopperComponent}
        ListboxProps={{ style: { maxHeight: "300px", overflow: "auto" } }}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.id}>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
            {option.label}
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            placeholder={hasFieldValues ? "" : "All cost centers (no restrictions)"}
          />
        )}
      />
    </SelectAllContext.Provider>
  );
}
